// HeroAgentOrbit.jsx — premium hero visual: a business application operated by a ring of AI agents.
// Vector + subtle motion (SMIL flow dots, CSS pulse/float/ring-spin). Brand palette.
const INK = "#3E2818";
const CREAM = "#F7F5F0";
const TERRA = "#D1633D";
const AMBER = "#E0902B";
const GREY = "#B8B0A4";
const MONO = "'IBM Plex Mono', ui-monospace, monospace";

const CX = 300;
const CY = 260;
const R = 196; // agent ring radius

const AGENTS = [
  { ang: -90, name: "Validator", verb: "3-way match", c: TERRA, dir: "in" },
  { ang: -30, name: "Approver", verb: "policy gate", c: AMBER, dir: "out" },
  { ang: 30, name: "Executor", verb: "post to ERP", c: "#4E6E9E", dir: "out" },
  { ang: 90, name: "Notifier", verb: "alert owner", c: "#7FA55E", dir: "out" },
  { ang: 150, name: "Reconciler", verb: "match ledger", c: TERRA, dir: "in" },
  { ang: 210, name: "Auditor", verb: "log & trace", c: "#C25E80", dir: "in" },
];

const pt = (ang, r) => {
  const rad = (ang * Math.PI) / 180;
  return { x: CX + r * Math.cos(rad), y: CY + r * Math.sin(rad) };
};

export default function HeroAgentOrbit({ className }) {
  return (
    <svg viewBox="0 0 600 560" className={className} role="img" aria-label="AI agents operating a business application">
      <style>{`
        @keyframes hao-float { 0%,100%{ transform: translateY(0) } 50%{ transform: translateY(-6px) } }
        @keyframes hao-spin { to { transform: rotate(360deg) } }
        @keyframes hao-pulse { 0%,100%{ opacity:.0; transform: scale(1) } 50%{ opacity:.5; transform: scale(1.35) } }
        @keyframes hao-dot { 0%,100%{ opacity:.25 } 50%{ opacity:1 } }
        .hao-app { animation: hao-float 6s ease-in-out infinite; transform-box: fill-box; transform-origin: center; }
        .hao-ring { animation: hao-spin 60s linear infinite; transform-box: fill-box; transform-origin: ${CX}px ${CY}px; }
        .hao-glow { transform-box: fill-box; transform-origin: center; }
        .hao-p0{ animation: hao-pulse 3s ease-in-out infinite; transform-box: fill-box; transform-origin:center }
        .hao-p1{ animation: hao-pulse 3s ease-in-out .5s infinite; transform-box: fill-box; transform-origin:center }
        .hao-p2{ animation: hao-pulse 3s ease-in-out 1s infinite; transform-box: fill-box; transform-origin:center }
        .hao-p3{ animation: hao-pulse 3s ease-in-out 1.5s infinite; transform-box: fill-box; transform-origin:center }
        .hao-p4{ animation: hao-pulse 3s ease-in-out 2s infinite; transform-box: fill-box; transform-origin:center }
        .hao-p5{ animation: hao-pulse 3s ease-in-out 2.5s infinite; transform-box: fill-box; transform-origin:center }
      `}</style>

      <defs>
        <radialGradient id="hao-core" cx="50%" cy="50%" r="50%">
          <stop offset="0%" stopColor={TERRA} stopOpacity="0.14" />
          <stop offset="55%" stopColor={AMBER} stopOpacity="0.06" />
          <stop offset="100%" stopColor={CREAM} stopOpacity="0" />
        </radialGradient>
      </defs>

      {/* soft radial glow */}
      <circle className="hao-glow" cx={CX} cy={CY} r="250" fill="url(#hao-core)" />

      {/* concentric orbit rings */}
      <circle cx={CX} cy={CY} r={R + 26} fill="none" stroke={INK} strokeOpacity="0.06" strokeWidth="1" />
      <circle className="hao-ring" cx={CX} cy={CY} r={R} fill="none" stroke={GREY} strokeWidth="1" strokeDasharray="2 7" strokeOpacity="0.8" />
      <circle cx={CX} cy={CY} r={R - 30} fill="none" stroke={INK} strokeOpacity="0.05" strokeWidth="1" />

      {/* outer arc label */}
      <path id="hao-arc" d={`M ${CX - (R + 26)} ${CY} A ${R + 26} ${R + 26} 0 0 1 ${CX + (R + 26)} ${CY}`} fill="none" />
      <text fontFamily={MONO} fontSize="10.5" letterSpacing="4" fill={TERRA}>
        <textPath href="#hao-arc" startOffset="50%" textAnchor="middle">AGENTIC&#160;&#160;LAYER</textPath>
      </text>

      {/* connectors + flow dots */}
      {AGENTS.map((a, i) => {
        const s = pt(a.ang, 118); // start (app edge)
        const e = pt(a.ang, R - 34); // end (chip inner edge)
        const id = `hao-conn-${i}`;
        return (
          <g key={`c-${i}`}>
            <path id={id} d={`M ${s.x} ${s.y} L ${e.x} ${e.y}`} fill="none" stroke={a.c} strokeOpacity="0.4" strokeWidth="1.25" />
            <circle r="3" fill={a.c}>
              <animateMotion dur="2.4s" repeatCount="indefinite" keyPoints={a.dir === "in" ? "1;0" : "0;1"} keyTimes="0;1" calcMode="linear">
                <mpath href={`#${id}`} />
              </animateMotion>
            </circle>
          </g>
        );
      })}

      {/* central application window */}
      <g className="hao-app">
        <rect x={CX - 100} y={CY - 76} width="200" height="152" rx="14" fill="#fff" stroke={INK} strokeOpacity="0.12" />
        <rect x={CX - 100} y={CY - 76} width="200" height="152" rx="14" fill="none" stroke={INK} strokeOpacity="0.04" />
        {/* title bar */}
        <rect x={CX - 100} y={CY - 76} width="200" height="30" rx="14" fill="#FBF7F1" />
        <rect x={CX - 100} y={CY - 62} width="200" height="16" fill="#FBF7F1" />
        <circle cx={CX - 84} cy={CY - 61} r="3.2" fill="#E4A0A0" />
        <circle cx={CX - 73} cy={CY - 61} r="3.2" fill="#E6C787" />
        <circle cx={CX - 62} cy={CY - 61} r="3.2" fill="#A9CE8E" />
        <text x={CX + 84} y={CY - 57} textAnchor="end" fontFamily={MONO} fontSize="8.5" letterSpacing="1" fill={INK} opacity="0.55">ACCOUNTS PAYABLE</text>
        {/* record row */}
        <text x={CX - 84} y={CY - 30} fontFamily={MONO} fontSize="8" fill={GREY}>INVOICE</text>
        <text x={CX - 84} y={CY - 16} fontFamily="'IBM Plex Sans',sans-serif" fontSize="12" fontWeight="600" fill={INK}>INV-2231 · Acme</text>
        <text x={CX + 84} y={CY - 16} textAnchor="end" fontFamily="'IBM Plex Sans',sans-serif" fontSize="12" fontWeight="700" fill={INK}>$48,200</text>
        <line x1={CX - 84} y1={CY - 4} x2={CX + 84} y2={CY - 4} stroke={INK} strokeOpacity="0.08" />
        {/* progress / agent activity */}
        <rect x={CX - 84} y={CY + 6} width="168" height="7" rx="3.5" fill="#EFEAE2" />
        <rect x={CX - 84} y={CY + 6} width="120" height="7" rx="3.5" fill={TERRA} />
        <text x={CX - 84} y={CY + 30} fontFamily={MONO} fontSize="7.5" fill={GREY}>3-way match &#183; approval &#183; posting</text>
        {/* status pill */}
        <rect x={CX - 84} y={CY + 42} width="120" height="22" rx="11" fill="#FCEFE8" stroke={TERRA} strokeOpacity="0.5" />
        <circle cx={CX - 72} cy={CY + 53} r="3.5" fill={TERRA} />
        <text x={CX - 62} y={CY + 56} fontFamily={MONO} fontSize="8" letterSpacing="0.5" fill={TERRA}>AGENT-OPERATED</text>
      </g>

      {/* agent cards */}
      {AGENTS.map((a, i) => {
        const c = pt(a.ang, R);
        const w = 128, h = 44;
        const x = c.x - w / 2, y = c.y - h / 2;
        return (
          <g key={`a-${i}`}>
            {/* pulse halo */}
            <circle className={`hao-p${i}`} cx={c.x} cy={c.y} r="26" fill={a.c} />
            <rect x={x} y={y} width={w} height={h} rx="12" fill={CREAM} stroke={a.c} strokeWidth="1.5" />
            <rect x={x} y={y} width={w} height={h} rx="12" fill="#fff" fillOpacity="0.35" />
            {/* alpha badge */}
            <circle cx={x + 22} cy={c.y} r="13" fill={a.c} />
            <text x={x + 22} y={c.y + 4.5} textAnchor="middle" fontFamily={MONO} fontSize="14" fill="#fff">&#945;</text>
            {/* labels */}
            <text x={x + 42} y={c.y - 3} fontFamily="'IBM Plex Sans',sans-serif" fontSize="12" fontWeight="700" fill={INK}>{a.name}</text>
            <text x={x + 42} y={c.y + 11} fontFamily={MONO} fontSize="8" fill={INK} opacity="0.55">{a.verb}</text>
            {/* status dot */}
            <circle cx={x + w - 12} cy={y + 12} r="3" fill={a.c}>
              <animate attributeName="opacity" values="0.3;1;0.3" dur="2s" begin={`${i * 0.3}s`} repeatCount="indefinite" />
            </circle>
          </g>
        );
      })}
    </svg>
  );
}
